import React, { useCallback, useEffect, useState } from "react";
import { Popper } from "@remo-co/ui-core/src/components/Popper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Card } from "@remo-co/ui-core/src/components/Card";
import classNames from "classnames";
import { useI18n } from "i18n";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import {
  updateGlobalUserPreference,
  DNS_KEYS,
} from "modules/globalUserPreference";
import { useSelector } from "react-redux";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
import { useStyles } from "./FirstTimeUserNudgePopover.styles";

interface IFirstTimeUserNudgePopoverProps {
  containerRef: React.MutableRefObject<HTMLButtonElement | null>;
  presentationMode?: boolean;
}

const { hasClosedFirstTimeEventDirectoryNudge } = DNS_KEYS;

const FirstTimeUserNudgePopover = ({
  containerRef,
  presentationMode,
}: IFirstTimeUserNudgePopoverProps) => {
  const [isFirstTimeUserNudgePopoverVisible, setPopoverVisible] =
    useState(false);
  const { t } = useI18n(["eventDirectory"]);
  const styles = useStyles();
  const currentUserPreferences = useSelector(selectCurrentUserPreferences);
  const isPopoverDismissed = useCallback(
    () =>
      currentUserPreferences &&
      currentUserPreferences[hasClosedFirstTimeEventDirectoryNudge],
    [currentUserPreferences],
  );

  useEffect(() => {
    if (!isPopoverDismissed()) {
      setPopoverVisible(true);
    }
  }, [isPopoverDismissed]);

  const onPopoverClose = () => {
    setPopoverVisible(false);
    updateGlobalUserPreference({
      [hasClosedFirstTimeEventDirectoryNudge]: true,
    });
  };

  return (
    <Popper
      anchorEl={containerRef.current}
      popperOptions={{
        modifiers: [
          {
            name: "offset",
            enabled: true,
            options: {
              offset: [0, 12],
            },
          },
          {
            name: "arrow",
            enabled: true,
          },
        ],
      }}
      placement="bottom-end"
      open={isFirstTimeUserNudgePopoverVisible}
      disablePortal
      className={classNames(styles.popoverPopper)}
    >
      <Card
        size="xs"
        flex
        fullWidth={false}
        className={classNames(styles.popoverCard, {
          [styles.popoverCardPresentationMode]: presentationMode,
        })}
      >
        <Typography
          variant="body1"
          color="textPrimary"
          className={classNames({
            [styles.textPresentationMode]: presentationMode,
          })}
        >
          {t("eventDirectory:jump.between.events")}
        </Typography>
        <Button
          isIconButton
          variant="ghost"
          color="dark"
          size="sm"
          data-testid="close-popover-button"
          onClick={onPopoverClose}
          className={classNames(styles.closeButton, {
            [styles.closeButtonPresentationMode]: presentationMode,
          })}
        >
          <Close />
        </Button>
      </Card>
    </Popper>
  );
};

export default FirstTimeUserNudgePopover;
